import axios from 'axios';
import { fromJS, List, Map, Set } from 'immutable';
import { dispatch } from 'nike-re-framejs';

import { getUrl } from '@/common/oldMobile';
import { loadSeenTournaments, loadTournaments, storeSeenTournaments } from '@/tournaments/effects';
import { paramsSerializer } from '@/_http/utils';

export const loadTournamentWithRanking = tournamentId =>
    axios.get(getUrl('TOURNAMENT_WITH_LEADERBOARD').replace('{tournamentId}', tournamentId)).then(data => {
        dispatch(rankingLoaded(data));
    });

export const loadTickets = (tournamentId, date, offset = 0, limit = 50) => db => {
    let url = date
        ? getUrl('TOURNAMENT_TICKETS_WITH_DATE')
              .replace('{tournamentId}', tournamentId)
              .replace('{inDate}', date.format('yyyy-MM-DD'))
        : getUrl('TOURNAMENT_TICKETS').replace('{tournamentId}', tournamentId);
    url += `?offset=${offset}&limit=${limit}`;
    axios.get(url).then(tickets =>
        dispatch(db => {
            if (offset === 0) {
                return db.setIn(['tournaments', 'tickets', tournamentId], fromJS(tickets));
            } else {
                const ticks = db.getIn(['tournaments', 'tickets', tournamentId]);
                return db.setIn(['tournaments', 'tickets', tournamentId], ticks.push(...tickets));
            }
        })
    );
    return db;
};
export const loadTicketsCount = (tournamentId, date) => db => {
    const url = date
        ? getUrl('TOURNAMENT_TICKETS_COUNT_WITH_DATE')
              .replace('{tournamentId}', tournamentId)
              .replace('{inDate}', date.format('yyyy-MM-DD'))
        : getUrl('TOURNAMENT_TICKETS_COUNT').replace('{tournamentId}', tournamentId);
    axios.get(url).then(counts => dispatch(db => db.setIn(['tournaments', 'ticketCounts', tournamentId], counts)));
    return db;
};

export const loadTournamentCounts = db => {
    if (!db.getIn(['tournaments', 'availableCounts', 'loaded'])) {
        axios
            .get(getUrl('TOURNAMENTS'), {
                params: { light: true, statusRange: ['Ready', 'Active'] },
                paramsSerializer: paramsSerializer.traditional
            })
            .then(tournaments => dispatch(computeAvailableCounts(tournaments)));
        return db.setIn(['tournaments', 'availableCounts', 'loaded'], true);
    }
};
export const setTicketsFilter = filter => db => db.setIn(['tournaments', 'tickets', 'filter'], filter);

export function isNewAvailable(tournament) {
    return (tournament.status === 'Ready' || tournament.status === 'Active') && tournament.optin === false;
}

const computeAvailableCounts = tournaments => db => {
    const availableTournaments = tournaments.filter(t => t.status === 'Ready' || t.status === 'Active');
    const newIds = availableTournaments.filter(isNewAvailable).map(t => t.id);
    const seenIds = Set(loadSeenTournaments());
    const unseenCount = Set(newIds).subtract(seenIds).size;
    return db
        .setIn(['tournaments', 'availableCounts', 'ids'], Set(availableTournaments.map(t => t.id)))
        .setIn(['tournaments', 'availableCounts', 'unseen'], unseenCount)
        .setIn(['tournaments', 'availableCounts', 'ready'], tournaments.filter(t => t.status === 'Ready').length)
        .setIn(['tournaments', 'availableCounts', 'active'], tournaments.filter(t => t.status === 'Active').length)
        .deleteIn(['tournaments', 'disabled']);
};

export const tournamentsSetAvailableAsSeen = db => {
    const availableTournaments = db.getIn(['tournaments', 'availableCounts', 'ids']);
    return [db.setIn(['tournaments', 'availableCounts', 'unseen'], 0), storeSeenTournaments(availableTournaments)];
};

function split(tournaments) {
    const reduceIds = (list, tournament) => list.push(tournament.id);
    const ready = tournaments.filter(t => t.status === 'Ready').reduce(reduceIds, List());
    const active = tournaments.filter(t => t.status === 'Active').reduce(reduceIds, List());
    const finished = tournaments.filter(t => t.status === 'Finished').reduce(reduceIds, List());
    const byIds = tournaments.reduce((map, tournament) => {
        return map.set(tournament.id, fromJS(tournament));
    }, Map());
    return { byIds, ready, active, finished };
}

export const availableTournamentsLoaded = tournaments => db => {
    const { byIds, ready, active } = split(tournaments);
    return [
        db
            .mergeIn(['tournaments', 'tournaments', 'byIds'], byIds)
            .setIn(['tournaments', 'tournaments', 'ready'], ready.reverse())
            .setIn(['tournaments', 'tournaments', 'active'], active.reverse()),
        computeAvailableCounts(tournaments)
    ];
};

export const finishedTournamentsLoaded = tournaments => db => {
    const { byIds, finished } = split(tournaments);
    return db
        .mergeIn(['tournaments', 'tournaments', 'byIds'], byIds)
        .setIn(['tournaments', 'tournaments', 'finished'], finished)
        .deleteIn(['tournaments', 'disabled']);
};

const rankingLoaded = tournament => db => {
    //leaderboard si ulozime zvlast
    const ranking = tournament.leaderboard;
    delete tournament.leaderboard;
    return db
        .setIn(['tournaments', 'tournaments', 'byIds', tournament.id], fromJS(tournament))
        .setIn(['tournaments', 'tournaments', 'rankings'], fromJS({ [tournament.id]: ranking }));
};

export const rewardsLoaded = data => db => db.setIn(['tournaments', 'rewards'], data);

export const optedIn = tournamentId => db =>
    db.setIn(['tournaments', 'tournaments', 'byIds', tournamentId, 'optin'], true);

export const initAvailableTournaments = db => [
    db.setIn(['tournaments', 'availableCounts', 'loaded'], true),
    () => {
        loadTournaments(['Ready', 'Active'])
            .then(tournaments => dispatch(availableTournamentsLoaded(tournaments)))
            .catch(() => dispatch(onTournamentsRequestError));
    }
];

export const initFinishedTournaments = db => [
    db.updateIn(['tournaments', 'tournaments', 'finished'], (list = null) => list),
    () => {
        loadTournaments(['Finished'])
            .then(tournaments => dispatch(finishedTournamentsLoaded(tournaments)))
            .catch(() => dispatch(onTournamentsRequestError));
    }
];

const onTournamentsRequestError = db => db.setIn(['tournaments', 'disabled'], true);
